/**
 * ████████╗ ██████╗ ██╗     ██╗███╗   ██╗ ██████╗
 * ╚══██╔══╝██╔═══██╗██║     ██║████╗  ██║██╔═══██╗
 *    ██║   ██║   ██║██║     ██║██╔██╗ ██║██║   ██║
 *    ██║   ██║   ██║██║     ██║██║╚██╗██║██║   ██║
 *    ██║   ╚██████╔╝███████╗██║██║ ╚████║╚██████╔╝
 *    ╚═╝    ╚═════╝ ╚══════╝╚═╝╚═╝  ╚═══╝ ╚═════╝
 *
 * (c) Copyright 2021-present Rakuten Kobo Inc. (https://www.kobo.com)
 */

// -----------------------------------------------------------------------------
// IMPORTS
// -----------------------------------------------------------------------------

// External/Third-party dependencies
import React from 'react';
import { createRoot } from 'react-dom/client';

// Side effect import to configure locator for this app
import "@services/locator/services.samplereader";

// Internal dependencies
import GLOBALS from './GLOBALS';
import { locateService } from '@services/locator/locator';
import { isSupportedByPlatform } from '@modules/rx-tolino-epub/TTSManager';
import AppContainer from './AppContainer';
import { initViewportHeightFix } from '@rakuten/common-util/dom';


// -----------------------------------------------------------------------------
// CONFIGURATION
// -----------------------------------------------------------------------------

// The ID of the DOM node under which our app will be renderd
const DOM_CONTAINER_ID = 'root';

(function () {
  /**
   * // BUG: Chrome in iOS
   * Please check the function for more details.
  */
  initViewportHeightFix({ splashScreenSelector: "#splash-screen > svg" })

  return locateService(["logger","localConfig"])
    .then(([logger, localConfig]) => {
      localConfig.populateLocalConfig(GLOBALS);
      const appConfig = localConfig.getLocalConfig();

        return locateService([
          'platform',
          'queryString',
          'featureFlag',
          'legacyHelper'
        ]).then(([platform, queryString, featureFlag, legacyService]) => {
          // We need to transform old invocation calls of the application into the
          // new way to start the application. This is necessary because not all
          // booksellers will transform the call to this application right away.
          const legacyURL = legacyService.transformLegacyInvocation(window.location.href);
          if (legacyURL) {
            window.history.replaceState({ }, appConfig.APPLICATION.NAME, legacyURL);
          }

          // Parse URL parameters
          queryString.parseQueryString();

          // Configure log levels and log output
          // NOTE: Setting the log level is probably the last point in the code where
          //       you should use the native `console` object methods. From then on,
          //       use `logger` service by importing it and using the `.log`,
          //       `.info` etc. methods.
          logger.reRouteErrorHandler();
          logger.setLogLevel(queryString.getQueryParameter('loglevel') || GLOBALS.LOGGING.DEFAULT_LEVEL);

          // Define the platform this app is running on
          // TODO: Platform information is very basic atm. Add relevant information
          platform.setPlatform(GLOBALS.PLATFORM.WEB);

          // Announce the disabled features
          const disabledFeatures = [
            appConfig.FEATURES.ANALYTICS_GOOGLE_ANALYTICS,
            appConfig.FEATURES.ANALYTICS_MONITORING,
            appConfig.FEATURES.ANALYTICS_TRACKING,
            appConfig.FEATURES.DRM_KOBO,
            appConfig.FEATURES.RX_DOGEARS,
            appConfig.FEATURES.RX_EPUB2FXL,
            appConfig.FEATURES.RX_HIGHLIGHTS
          ];
          if (
            !isSupportedByPlatform() ||
            platform.isBrowserOnAndroid()
          ) {
            disabledFeatures.push(appConfig.FEATURES.RX_TTS);
          }
          featureFlag.setDisabledFeatures(disabledFeatures);

          // Import services that are used synchronously at a later point, similar to
          // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Statements/import#import_a_module_for_its_side_effects_only
          return locateService([
            'messageBus',
            'decompress'])
            .then(() => {
              // Initialize React app/lifecycle
              const rootDomElement = document.getElementById(DOM_CONTAINER_ID)
              const root = createRoot(rootDomElement);
              const render = () => {
                root.render(<AppContainer key={ Date.now() } />);
              };
              render();

              // Global exposure of data and methods to be used in dev console
              window.kobo = {
                appinfo: {
                  APP_VERSION: GLOBALS.APPLICATION.VERSION,
                  REACT_VERSION: React.version
                },
                buildinfo: GLOBALS.BUILDINFO,
                develop: {
                  disableFeature: featureName => {
                    featureFlag.addDisabledFeature(featureName);
                    render();
                  },
                  enableFeature: featureName => {
                    featureFlag.removeDisabledFeature(featureName);
                    render();
                  },
                  enabledFeatures: () => {
                    return featureFlag.getApplicationFeatures().filter(feature => {
                      return !featureFlag.getDisabledFeatures().includes(feature);
                    })
                  },
                  setLogLevel: logger.setLogLevel
                }
              };

              // Hide/Remove splash screen
              const splashScreen = document.getElementById('splash-screen');
              splashScreen.classList.add('invisible');
            });
        });
    });
})();
